import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, Users, Sparkles, Heart } from "lucide-react";
import { api, apiError, money } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import MusicianMatchCard from "@/components/MusicianMatchCard";

// context: { event_id?, date?, start_time?, end_time?, lat?, lng?, roles:[{instrument,quantity,cache,min_level}] }
export default function TeamBuilder({ open, onOpenChange, mode = "match", context = {}, preselected = [], onConfirm }) {
  const [tab, setTab] = useState(mode);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);       // build-team response
  const [team, setTeam] = useState(null);        // team availability response
  const [sel, setSel] = useState({});            // user_id -> {stage_name}

  useEffect(() => { if (open) { setTab(mode); setSel(Object.fromEntries((preselected || []).map((p) => [p.user_id, p]))); } }, [open, mode]);

  const ctxBody = () => ({
    event_id: context.event_id || null, date: context.date || null,
    start_time: context.start_time || null, end_time: context.end_time || null,
    lat: context.lat ?? null, lng: context.lng ?? null,
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true); setData(null); setTeam(null);
    if (tab === "match") {
      api.post("/match/build-team", { ...ctxBody(), roles: context.roles || [] })
        .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
    } else {
      api.post("/team/availability", ctxBody())
        .then((r) => setTeam(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))).finally(() => setLoading(false));
    }
  }, [open, tab]);

  const toggle = (m) => setSel((p) => {
    const n = { ...p };
    if (n[m.user_id]) delete n[m.user_id]; else n[m.user_id] = { user_id: m.user_id, stage_name: m.stage_name };
    return n;
  });

  const confirm = () => { onConfirm?.(Object.values(sel)); onOpenChange(false); };

  const selCount = Object.keys(sel).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="team-builder-dialog" className="max-w-2xl max-h-[88vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-primary" />Montar minha equipe</DialogTitle>
          <DialogDescription>Selecione os músicos — eles receberão um convite assim que o evento for publicado.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 shrink-0">
          <button data-testid="tb-tab-match" onClick={() => setTab("match")} className={`flex-1 h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border transition-colors ${tab === "match" ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground"}`}><Sparkles className="h-4 w-4" />Sugestão (Match)</button>
          <button data-testid="tb-tab-team" onClick={() => setTab("team")} className={`flex-1 h-10 rounded-lg text-sm font-semibold flex items-center justify-center gap-2 border transition-colors ${tab === "team" ? "bg-primary/10 border-primary/40 text-primary" : "border-border text-muted-foreground"}`}><Heart className="h-4 w-4" />Chamar minha equipe</button>
        </div>

        <div className="overflow-y-auto flex-1 -mx-1 px-1 space-y-4 py-1">
          {loading && <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>}

          {!loading && tab === "match" && data && (
            <>
              {(!context.roles || context.roles.length === 0) && <p className="text-sm text-amber-400">Adicione ao menos uma vaga (instrumento) para gerar sugestões.</p>}
              {(data.options || []).map((opt) => (
                <div key={opt.instrument} data-testid={`tb-role-${opt.instrument}`}>
                  <h4 className="text-xs uppercase tracking-widest text-muted-foreground font-bold mb-2">{opt.instrument} · {opt.quantity} vaga(s)</h4>
                  {opt.candidates.length === 0 ? <p className="text-sm text-muted-foreground">Nenhum músico compatível encontrado.</p> : (
                    <div className="grid gap-2 sm:grid-cols-2">
                      {opt.candidates.map((m) => <MusicianMatchCard key={m.user_id} m={m} selected={!!sel[m.user_id]} onToggleSelect={toggle} />)}
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {!loading && tab === "team" && team && (
            team.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground"><Users className="h-8 w-8 mx-auto mb-2 opacity-60" />Sua equipe está vazia. Salve músicos favoritos em "Minha Equipe".</div>
            ) : (
              <div className="grid gap-2 sm:grid-cols-2">
                {team.map((m) => <MusicianMatchCard key={m.user_id} m={{ ...m, compatibility: undefined }} selected={!!sel[m.user_id]} onToggleSelect={toggle} />)}
              </div>
            )
          )}
        </div>

        <DialogFooter className="shrink-0 flex-row justify-between items-center gap-2 border-t border-border pt-3">
          <span className="text-sm text-muted-foreground" data-testid="tb-selected-count">{selCount} selecionado(s)</span>
          <Button data-testid="tb-confirm" onClick={confirm} disabled={selCount === 0}>Adicionar {selCount > 0 ? `(${selCount})` : ""} à lista</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
